import { Outlet, NavLink, useLocation, useNavigate, Navigate } from "react-router-dom";
import { LayoutDashboard, Package, PackageSearch, PackageCheck, Users, LogOut, Leaf, GitCompareArrows, Truck, ClipboardList, ClipboardPlus, BarChart3, Settings, Wallet, UserCheck, Braces } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";

const TITLES = {
  "/": "Genel Bakış",
  "/urun-bazinda": "Tüm Siparişler",
  "/siparisler": "Satınalmalar",
  "/rapor": "Rapor",
  "/finans": "Finansal Rapor",
  "/mal-kabul": "Mal Kabul",
  "/ayarlar": "Ayarlar",
  "/api": "API",
  "/katalog": "Ürün Kataloğu",
  "/uyum-raporu": "Uyum Raporu",
  "/kullanicilar": "Kullanıcı Yönetimi",
  "/tedarik-siparisleri": "Tedarik Siparişlerim",
};

export const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={testId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 text-sm border-l-2 transition-colors duration-200 ${
        isActive
          ? "border-[#5BAE7F] bg-white/5 text-white"
          : "border-transparent text-stone-400 hover:bg-white/5 hover:text-white"
      }`
    }
  >
    <Icon size={17} strokeWidth={1.8} />
    <span>{label}</span>
  </NavLink>
);

export default function Layout() {
  const { user, logout, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const title = TITLES[location.pathname] || "COP31 Portal";
  const isAdmin = user?.role === "admin";
  const isSupplier = user?.role === "supplier";
  const isTracker = user?.role === "malkabul" || user?.role === "satinalma";
  const isLimited = isTracker || user?.role === "izleyici";
  const canTracking = user?.email === "mbucak@gmail.com";
  const hiddenMenus = new Set(canTracking ? [] : user?.hidden_menus || []);
  const PATH_KEY = {
    "/": "dashboard", "/urun-bazinda": "orders", "/rapor": "reports", "/finans": "finance",
    "/ayarlar": "settings", "/katalog": "catalog", "/uyum-raporu": "reconciliation",
    "/kullanicilar": "users", "/tedarik-siparisleri": "supplier_orders", "/mal-kabul": "goods_receipt",
  };

  if (!canTracking && location.pathname === "/siparisler") {
    return <Navigate to={isLimited || isSupplier ? "/urun-bazinda" : "/"} replace />;
  }
  if (isLimited && location.pathname !== "/urun-bazinda" && !(canTracking && location.pathname === "/siparisler") && !(user?.role === "malkabul" && location.pathname === "/mal-kabul")) {
    return <Navigate to="/urun-bazinda" replace />;
  }
  const curKey = PATH_KEY[location.pathname];
  if (curKey && hiddenMenus.has(curKey)) {
    const fallbacks = isSupplier ? ["/tedarik-siparisleri"] : isLimited ? ["/urun-bazinda"] : ["/", "/urun-bazinda", "/rapor", "/katalog", "/uyum-raporu"];
    const target = fallbacks.find((p) => !hiddenMenus.has(PATH_KEY[p]) && p !== location.pathname);
    if (target) return <Navigate to={target} replace />;
  }

  return (
    <div className="flex min-h-screen bg-[#F7F7F6]">
      <aside
        className="fixed inset-y-0 left-0 z-30 hidden w-60 flex-col bg-[#0A0D0B] md:flex"
        data-testid="sidebar"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,13,11,0.94), rgba(10,13,11,0.94)), url(https://images.unsplash.com/photo-1652120268427-9ba66e870467?crop=entropy&cs=srgb&fm=jpg&q=60&w=600)",
          backgroundSize: "cover",
        }}
      >
        <div className="flex items-center gap-2.5 border-b border-[#2A2D2B] px-5 py-6">
          <div className="flex h-9 w-9 items-center justify-center bg-[#1C4E35]">
            <Leaf size={18} className="text-[#8FD4AC]" strokeWidth={2} />
          </div>
          <div>
            <p className="font-heading text-sm font-black tracking-tight text-white">COP31 PORTAL</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Rate Card Sistemi</p>
          </div>
        </div>
        <nav className="mt-4 flex-1">
          {isSupplier ? (
            <NavItem to="/tedarik-siparisleri" icon={Truck} label="Siparişlerim" testId="nav-supplier-orders" />
          ) : isLimited ? (
            <>
              <NavItem to="/urun-bazinda" icon={PackageSearch} label="Tüm Siparişler" testId="nav-by-product" />
              {user?.role === "malkabul" && !hiddenMenus.has("goods_receipt") && <NavItem to="/mal-kabul" icon={PackageCheck} label="Mal Kabul" testId="nav-malkabul" />}
              {canTracking && <NavItem to="/siparisler" icon={ClipboardList} label="Satınalmalar" testId="nav-tracking" />}
            </>
          ) : (
            <>
              {!hiddenMenus.has("dashboard") && <NavItem to="/" icon={LayoutDashboard} label="Genel Bakış" testId="nav-dashboard" />}
              {!hiddenMenus.has("orders") && <NavItem to="/urun-bazinda" icon={PackageSearch} label="Tüm Siparişler" testId="nav-by-product" />}
              {canTracking && <NavItem to="/siparisler" icon={ClipboardList} label="Satınalmalar" testId="nav-tracking" />}
              {isAdmin && !hiddenMenus.has("goods_receipt") && <NavItem to="/mal-kabul" icon={PackageCheck} label="Mal Kabul" testId="nav-malkabul" />}
              {isAdmin && <NavItem to="/manuel" icon={ClipboardPlus} label="Manuel Ekleme" testId="nav-manual" />}
              {!hiddenMenus.has("reports") && <NavItem to="/rapor" icon={BarChart3} label="Rapor" testId="nav-reports" />}
              {isAdmin && !hiddenMenus.has("finance") && <NavItem to="/finans" icon={Wallet} label="Finansal Rapor" testId="nav-finance" />}
              {isAdmin && !hiddenMenus.has("settings") && <NavItem to="/ayarlar" icon={Settings} label="Ayarlar" testId="nav-settings" />}
              {isAdmin && <NavItem to="/api" icon={Braces} label="API" testId="nav-api" />}
              {!hiddenMenus.has("catalog") && <NavItem to="/katalog" icon={Package} label="Ürün Kataloğu" testId="nav-products" />}
              {!hiddenMenus.has("reconciliation") && <NavItem to="/uyum-raporu" icon={GitCompareArrows} label="Uyum Raporu" testId="nav-reconciliation" />}
              {isAdmin && !hiddenMenus.has("users") && <NavItem to="/kullanicilar" icon={Users} label="Kullanıcılar" testId="nav-users" />}
            </>
          )}
        </nav>
        <div className="border-t border-[#2A2D2B] px-5 py-4">
          <p className="truncate text-xs font-medium text-stone-300" data-testid="sidebar-user-name">
            {user?.name}
          </p>
          <p className="truncate text-[11px] text-stone-500">{user?.email}</p>
          {user?.organisation && (
            <Badge className="mt-2 rounded-sm bg-[#1C4E35] text-[10px] text-white hover:bg-[#1C4E35]">
              {user.organisation}
            </Badge>
          )}
          {isAdmin && (
            <Badge className="mt-2 rounded-sm bg-[#1C4E35] text-[10px] text-white hover:bg-[#1C4E35]">Yönetici</Badge>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col md:pl-60">
        {user?.impersonated && (
          <div className="sticky top-0 z-30 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-amber-500 px-4 py-2 text-center" data-testid="impersonation-banner">
            <p className="text-xs font-semibold text-amber-950">
              <UserCheck size={13} className="mr-1 inline" />
              <strong>{user.name || user.email}</strong> hesabını görüntülüyorsunuz
            </p>
            <button
              type="button"
              onClick={async () => { await stopImpersonation(); navigate("/kullanicilar"); }}
              data-testid="stop-impersonation-button"
              className="border border-amber-950/40 bg-amber-950 px-2.5 py-1 text-[11px] font-semibold text-amber-50 transition-colors hover:bg-amber-900 active:scale-95"
            >
              Yönetici hesabıma dön
            </button>
          </div>
        )}
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-stone-200 bg-white px-6 py-4">
          <h1 className="font-heading text-xl font-bold tracking-tight text-[#0A0D0B]" data-testid="page-title">
            {title}
          </h1>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-stone-500 sm:block">{user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
              className="rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95"
            >
              <LogOut size={14} className="mr-1.5" /> Çıkış
            </Button>
          </div>
        </header>

        <div className="border-b border-stone-200 bg-white px-4 py-2 md:hidden">
          <nav className="flex gap-1 overflow-x-auto text-xs">
            {isSupplier ? (
              <NavLink to="/tedarik-siparisleri" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Siparişlerim</NavLink>
            ) : isLimited ? (
              <>
                <NavLink to="/urun-bazinda" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Tüm Siparişler</NavLink>
                {canTracking && <NavLink to="/siparisler" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Satınalmalar</NavLink>}
              </>
            ) : (
              <>
                {!hiddenMenus.has("dashboard") && <NavLink to="/" end className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Genel</NavLink>}
                {!hiddenMenus.has("orders") && <NavLink to="/urun-bazinda" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Tüm Siparişler</NavLink>}
                {canTracking && <NavLink to="/siparisler" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Satınalmalar</NavLink>}
                {!hiddenMenus.has("reports") && <NavLink to="/rapor" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Rapor</NavLink>}
                {isAdmin && !hiddenMenus.has("finance") && <NavLink to="/finans" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Finans</NavLink>}
                {isAdmin && !hiddenMenus.has("goods_receipt") && <NavLink to="/mal-kabul" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Mal Kabul</NavLink>}
                {isAdmin && !hiddenMenus.has("settings") && <NavLink to="/ayarlar" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Ayarlar</NavLink>}
                {isAdmin && <NavLink to="/api" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>API</NavLink>}
                {!hiddenMenus.has("catalog") && <NavLink to="/katalog" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Katalog</NavLink>}
                {!hiddenMenus.has("reconciliation") && <NavLink to="/uyum-raporu" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Uyum</NavLink>}
                {isAdmin && !hiddenMenus.has("users") && <NavLink to="/kullanicilar" className={({ isActive }) => `px-3 py-1.5 rounded-sm ${isActive ? "bg-[#1C4E35] text-white" : "text-stone-600"}`}>Kullanıcılar</NavLink>}
              </>
            )}
          </nav>
        </div>

        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
